/**
 * Focus Indicator Component
 * 
 * Visual feedback component showing focus and stability status.
 * Designed for minimal visual distraction - user should look at the book,
 * not the screen.
 * 
 * Features:
 * - Color-coded status (red/yellow/green)
 * - Progress ring showing capture readiness
 * - Human-readable guidance text
 * - Compact mode for embedded use
 */

import React from 'react';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import type { FocusStatus, AutoCaptureStatus } from '@/lib/scan';

// =============================================================================
// TYPES
// =============================================================================

export interface FocusIndicatorProps {
  /** Focus status */
  focusStatus: FocusStatus;
  /** Auto-capture status */
  captureStatus: AutoCaptureStatus;
  /** Progress toward capture (0-100) */
  captureProgress: number;
  /** Guidance text to display */
  guidance: string;
  /** Whether to show in compact mode */
  compact?: boolean;
  /** Custom className */
  className?: string;
}

// =============================================================================
// CONSTANTS
// =============================================================================

const STATUS_COLORS: Record<FocusStatus, { bg: string; text: string; ring: string }> = {
  blurry: {
    bg: 'bg-red-500/20',
    text: 'text-red-400',
    ring: 'stroke-red-500',
  },
  okay: {
    bg: 'bg-yellow-500/20',
    text: 'text-yellow-400',
    ring: 'stroke-yellow-500',
  },
  sharp: {
    bg: 'bg-green-500/20',
    text: 'text-green-400',
    ring: 'stroke-green-500',
  },
};

const CAPTURE_STATUS_ICONS: Record<AutoCaptureStatus, React.ReactNode> = {
  waiting: <Eye className="w-5 h-5" />,
  focusing: <EyeOff className="w-5 h-5" />,
  stabilizing: <Loader2 className="w-5 h-5 animate-spin" />,
  ready: <Eye className="w-5 h-5" />,
  cooldown: <Loader2 className="w-5 h-5 animate-spin" />,
  disabled: <EyeOff className="w-5 h-5" />,
};

// =============================================================================
// PROGRESS RING COMPONENT
// =============================================================================

interface ProgressRingProps {
  progress: number;
  size: number;
  strokeWidth: number;
  colorClass: string;
}

const ProgressRing: React.FC<ProgressRingProps> = ({
  progress,
  size,
  strokeWidth,
  colorClass,
}) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (progress / 100) * circumference;
  
  return (
    <svg
      className="absolute inset-0 -rotate-90"
      width={size}
      height={size}
    >
      {/* Background ring */}
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        className="text-white/20"
      />
      {/* Progress ring */}
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        className={`${colorClass} transition-all duration-150`}
      />
    </svg>
  );
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export const FocusIndicator: React.FC<FocusIndicatorProps> = ({
  focusStatus,
  captureStatus,
  captureProgress,
  guidance,
  compact = false,
  className = '',
}) => {
  const colors = STATUS_COLORS[focusStatus];
  const icon = CAPTURE_STATUS_ICONS[captureStatus];
  
  // Compact mode - just a small indicator
  if (compact) {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <div
          className={`relative w-8 h-8 rounded-full ${colors.bg} flex items-center justify-center`}
        >
          {captureProgress > 0 && captureProgress < 100 && (
            <ProgressRing
              progress={captureProgress}
              size={32}
              strokeWidth={3}
              colorClass={colors.ring}
            />
          )}
          <div className={colors.text}>
            {icon}
          </div>
        </div>
        <span className="text-white text-sm">{guidance}</span>
      </div>
    );
  }
  
  // Full mode - prominent display
  return (
    <div 
      className={`
        flex flex-col items-center gap-3 p-4 rounded-2xl
        backdrop-blur-sm transition-colors
        ${colors.bg}
        ${className}
      `}
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
    >
      {/* Status icon with progress ring */}
      <div className="relative w-16 h-16 flex items-center justify-center">
        {captureProgress > 0 && (
          <ProgressRing
            progress={captureProgress}
            size={64}
            strokeWidth={4}
            colorClass={colors.ring}
          />
        )}
        <div className={`relative ${colors.text}`}>
          {React.cloneElement(icon as React.ReactElement, {
            className: 'w-8 h-8',
          })}
        </div>
      </div>
      
      {/* Guidance text */}
      <div className="text-center">
        <p className="text-white font-medium text-lg">{guidance}</p>
        
        {/* Status label */}
        <p className={`text-sm mt-1 capitalize ${colors.text}`}>
          {captureStatus === 'cooldown' ? 'Turn page...' : focusStatus}
        </p>
      </div>
      
      {/* Progress bar (alternative visual) */}
      {captureProgress > 0 && captureProgress < 100 && (
        <div className="w-full max-w-[200px] h-2 bg-white/20 rounded-full overflow-hidden">
          <div
            className={`h-full transition-all duration-150 ${
              focusStatus === 'sharp' ? 'bg-green-500' :
              focusStatus === 'okay' ? 'bg-yellow-500' :
              'bg-red-500'
            }`}
            style={{ width: `${captureProgress}%` }}
          />
        </div>
      )}
    </div>
  );
};

// =============================================================================
// MINIMAL INDICATOR (for scanner header)
// =============================================================================

export interface MinimalFocusIndicatorProps {
  focusStatus: FocusStatus;
  captureProgress: number;
  className?: string;
}

export const MinimalFocusIndicator: React.FC<MinimalFocusIndicatorProps> = ({
  focusStatus,
  captureProgress,
  className = '',
}) => {
  const colors = STATUS_COLORS[focusStatus];
  
  return (
    <div className={`flex items-center gap-2 ${className}`}>
      {/* Dot indicator */}
      <div
        className={`w-3 h-3 rounded-full transition-colors ${
          focusStatus === 'sharp' ? 'bg-green-500' :
          focusStatus === 'okay' ? 'bg-yellow-500' :
          'bg-red-500'
        } ${focusStatus === 'blurry' ? 'animate-pulse' : ''}`}
      />
      
      {/* Progress bar */}
      {captureProgress > 0 && (
        <div className="w-16 h-1.5 bg-white/20 rounded-full overflow-hidden">
          <div
            className={`h-full transition-all duration-100 ${
              focusStatus === 'sharp' ? 'bg-green-500' :
              focusStatus === 'okay' ? 'bg-yellow-500' :
              'bg-red-500'
            }`}
            style={{ width: `${captureProgress}%` }}
          />
        </div>
      )}
    </div>
  );
};

// =============================================================================
// EXPORTS
// =============================================================================

export default FocusIndicator;
